/**
 * The compact bottom dock (F-NAV-02).
 *
 * A custom bar rather than a tab widget, matching the native shell: the four
 * roots are all mounted (F-NAV-03), so there is no single "tab panel" to point
 * a `tablist` at, and claiming tab semantics would promise a relationship the
 * markup cannot honour. Buttons plus `aria-current` describe exactly what is
 * true — four actions, one of them current.
 */

import type { ReactNode } from "react";
import { VisuallyHidden } from "@ff/ui";
import type { DestinationId } from "./destinations";
import { DESTINATIONS, DOCK_LABEL, dockHint } from "./destinations";
import styles from "./Dock.module.css";

export interface DockProps {
  readonly selected: DestinationId;
  readonly onSelect: (destination: DestinationId) => void;
}

/**
 * The equal-width guarantee of F-NAV-05, written where a test can read it.
 *
 * At accessibility text sizes the four targets must stay non-overlapping and
 * within 1 px of equal width. A four-column `1fr` grid is what makes that true
 * at every text size, and `minmax(0, 1fr)` rather than plain `1fr` is the part
 * that matters: a bare `1fr` track has an `auto` minimum, so the widest label
 * would push its own column wider than the other three exactly when the labels
 * grow.
 */
export const DOCK_GRID_TEMPLATE = "repeat(4, minmax(0, 1fr))";

export function Dock({ selected, onSelect }: DockProps): ReactNode {
  return (
    <nav
      aria-label={DOCK_LABEL}
      className={styles.dock}
      data-testid="dock"
      style={{ gridTemplateColumns: DOCK_GRID_TEMPLATE }}
    >
      {DESTINATIONS.map((destination) => {
        const isSelected = destination.id === selected;
        const hintId = `${destination.testId}.hint`;
        return (
          <button
            key={destination.id}
            type="button"
            data-testid={destination.testId}
            className={isSelected ? `${styles.item} ${styles.itemSelected}` : styles.item}
            aria-current={isSelected ? "page" : undefined}
            aria-describedby={hintId}
            onClick={() => {
              onSelect(destination.id);
            }}
          >
            {/* The 20x3 selection rule. Decorative: the state it depicts is
                already carried by `aria-current` and by the hint. */}
            <span className={styles.rule} aria-hidden="true" />
            <span className={styles.label}>{destination.title}</span>
          </button>
        );
      })}
      {/*
        The hints sit outside the buttons on purpose. A button's accessible name
        comes from its contents, so a hidden hint *inside* it would append
        itself to the name — "Train Switches to the Train tab" — and F-NAV-02
        wants the title as the label and the sentence as the description.
      */}
      {DESTINATIONS.map((destination) => (
        <VisuallyHidden key={destination.id} id={`${destination.testId}.hint`}>
          {dockHint(destination.title, destination.id === selected)}
        </VisuallyHidden>
      ))}
    </nav>
  );
}
